import { ROLE } from '@/shared/config';
import { isIncludeRole } from '@/entities/user';
import _ from 'lodash';
import { useMemo } from 'react';
import type { WarningLineProps } from './warningLine.types';

/**
 * Derives the concurrent-editing warning shown on a report or event page.
 * Counts the conflict users with edit rights, gates the notice to the event-details and
 * edit-report pages, words the read-only, be-careful or someone-is-viewing message, and flags
 * whether a notice exists and whether it uses the blue background.
 *
 * @param props - Warning props carrying conflictUsers, isNotCanEdit, isEventDetailsPage and
 * isEditReportPage.
 * @returns The editable-user count, the visibility flags, the message builder and the background
 * flag.
 */
export const useWarningLine = ({
  conflictUsers,
  isNotCanEdit,
  isEventDetailsPage,
  isEditReportPage,
}: Pick<
  WarningLineProps,
  'conflictUsers' | 'isNotCanEdit' | 'isEventDetailsPage' | 'isEditReportPage'
>) => {
  /**
   * Memoized count of users in the conflict list that hold editing rights on the record. Counting only happens when more than one conflict user is present: the first entry always counts, and each later entry counts only when its roles include the call-center QA role.
   *
   * @returns Number of conflict users considered to have edit rights.
   */
  const numberOfEditableUsers = useMemo(() => {
    let numberOfEditableUsers = 0;
    if (conflictUsers && conflictUsers.length > 1) {
      _.forEach(conflictUsers, (conflictUser, index) => {
        if (index === 0) {
          numberOfEditableUsers += 1;
        } else if (isIncludeRole(conflictUser?.roles, ROLE.CALL_CENTER_QA)) {
          numberOfEditableUsers += 1;
        }
      });
    }
    return numberOfEditableUsers;
  }, [conflictUsers]);

  /**
   * Memoized flag gating conflict notifications to the pages that support them. True on the event-details page or the edit-report page, recomputed when either page flag changes.
   *
   * @returns True on the event-details or edit-report page.
   */
  const shouldShowConflictNotification = useMemo(
    () => isEventDetailsPage || isEditReportPage,
    [isEventDetailsPage, isEditReportPage],
  );

  /**
   * Words the concurrent-editing warning for a report or event page. Picks the noun from whether the event details page is active, then returns the read-only message when editing is blocked, a be-careful message when more than one user may edit, and a plain someone-is-viewing message otherwise.
   *
   * @returns Warning sentence matching the current editing conflict.
   */
  const generateConflictNotification = () => {
    const pageType = isEventDetailsPage ? 'event' : 'report';
    if (isNotCanEdit) {
      return `Someone is editing this ${pageType}. You will not be able to make any changes to this ${pageType} at this time`;
    }
    if (numberOfEditableUsers > 1) {
      return `Be careful. Someone is editing this ${pageType}`;
    }
    return `Someone else is also viewing this ${pageType}.`;
  };

  /**
   * Memoized flag that is true when a conflict notification exists for the current record. Set when the user is blocked from editing or at least one editable user is present, and recomputed when the read-only flag or the conflict-user list changes.
   *
   * @returns True when a conflict notification should be produced.
   */
  const hasConflictNotification = useMemo(
    () => isNotCanEdit || numberOfEditableUsers > 0,
    [isNotCanEdit, conflictUsers],
  );

  /**
   * Memoized flag that is true when the current user may still edit while exactly one other user holds edit rights. Derived from the read-only flag and the editable-user count, and recomputed when the read-only flag or the conflict-user list changes.
   *
   * @returns True when editing is still allowed and exactly one other user is editing.
   */
  const hasBlueBackgroundColor = useMemo(
    () => !isNotCanEdit && numberOfEditableUsers === 1,
    [isNotCanEdit, conflictUsers],
  );

  return {
    numberOfEditableUsers,
    shouldShowConflictNotification,
    generateConflictNotification,
    hasConflictNotification,
    hasBlueBackgroundColor,
  };
};
